// src/components/profile/Documents/EthicsReportsCard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Lucide from "lucide-react";
import {
  Upload,
  FileText,
  Trash2,
  Edit2,
  Save,
  X,
  ArrowLeft,
  Eye,
  EyeOff,
  Plus,
  ChevronUp,
  ChevronDown,
  Image as ImageIcon,
  Wand2,
  Loader2,
  Info,
} from "lucide-react";
import Swal from "sweetalert2";
import {
  listEthicsDocs,
  uploadEthicsDoc,
  deleteEthicsDoc,
  updateEthicsDoc,
  downloadEthicsDocUrl,
  getEthicsConfig,
  updateEthicsConfig,
  getEthicsContent,
  updateEthicsContent,
} from "../../../services/ethicsCodeService";
import { useNavigate } from "react-router-dom";
import { Banner } from "../../common/Banner";
import TooltipHelper from "../../common/TooltipHelper";
import SectionTips from "../../common/SectionTips";

/* ---------- estilos / util (alineados a Prevention/Protocols) ---------- */
const input =
  "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const textareaBase =
  "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white [&:placeholder-shown]:placeholder:opacity-100";
const btn =
  "inline-flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap";
const btnPri = `${btn} bg-[#0488AF] text-white hover:bg-[#037a98] disabled:opacity-60 disabled:cursor-not-allowed`;
const btnGhost = `${btn} border bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed`;
const btnWarn = `${btn} border bg-white hover:bg-amber-50 text-amber-800 border-amber-300 disabled:opacity-60 disabled:cursor-not-allowed`;
const btnIcon =
  "px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40";
const btnDanger =
  "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50";
const btnAddBlue =
  "inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B4A8F] text-white hover:bg-[#093a70] shadow-sm";

const MAX_MB = 15;
const clone = (x) => JSON.parse(JSON.stringify(x || null));
const fmtBytes = (n) =>
  typeof n === "number" ? `${(n / 1024 / 1024).toFixed(2)} MB` : "";

/* ---------- swal helpers ---------- */
const showLoading = (title = "Procesando…") =>
  Swal.fire({
    title,
    html: "Por favor espera un momento.",
    allowOutsideClick: false,
    showConfirmButton: false,
    didOpen: () => Swal.showLoading(),
  });

const askConfirm = (opts = {}) =>
  Swal.fire({
    icon: "question",
    showCancelButton: true,
    confirmButtonText: opts.confirmButtonText || "Sí, continuar",
    cancelButtonText: "Cancelar",
    allowOutsideClick: false,
    ...opts,
  });

const toast = (title = "Listo", icon = "success", ms = 900) =>
  Swal.fire({ icon, title, timer: ms, showConfirmButton: false });

/* ---------- validaciones CTA ---------- */
const isHttpOrRoute = (s) => {
  if (!s) return true;
  if (s.startsWith("/")) return true;
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};
const isValidCtaUrl = (s) => {
  if (!s) return true;
  if (s.startsWith("mailto:")) return s.slice(7).includes("@");
  return isHttpOrRoute(s);
};

/* ---------- decoradores ---------- */
const Divider = () => (
  <div className="my-4">
    <div className="h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent rounded-full" />
  </div>
);


/* ---------- Switch ---------- */
const Switch = ({
  checked,
  onChange,
  titleOn = "Visible para socios",
  titleOff = "Oculto para socios",
}) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
      checked ? "bg-[#0488AF]" : "bg-gray-300"
    }`}
    title={checked ? titleOn : titleOff}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

/* ---------- iconos ---------- */
const CURATED_ICONS = [
  "ShieldCheck",
  "HandCoins",
  "Megaphone",
  "Scale",
  "Users",
  "ClipboardList",
  "FileText",
  "AlertTriangle",
  "Eye",
  "Lock",
  "Target",
];

const IconPreview = ({ name, size = 22, className }) => {
  const Cmp = (name && Lucide[name]) || FileText;
  return <Cmp size={size} className={className || "text-[#0B4A8F]"} />;
};

/* ---------- Picker ---------- */
function IconPicker({ open, value, onSelect, onClose }) {
  const [q, setQ] = useState("");
  const pool = CURATED_ICONS.map((n) => ({
    name: n,
    Cmp: Lucide[n] || FileText,
  }));
  const icons = !q.trim()
    ? pool
    : pool.filter((it) => it.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            className="relative w-[min(900px,92vw)] max-h-[84vh] overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="p-4 border-b bg-gradient-to-r from-[#E6F0FF] to-[#E6FBFF] flex items-center justify-between">
              <div className="font-semibold">Elegir ícono</div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/70"
              >
                <X />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar…"
                  className={`${input} max-w-sm`}
                />
                {value && (
                  <div className="ml-auto flex items-center gap-2 text-sm text-gray-600">
                    Actual: <IconPreview name={value} />
                    <code className="bg-gray-100 rounded px-2 py-1 text-xs">
                      {value}
                    </code>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 overflow-y-auto max-h-[58vh] pr-1">
                {icons.map(({ name, Cmp }) => (
                  <button
                    key={name}
                    onClick={() => {
                      onSelect?.(name);
                      onClose?.();
                    }}
                    className={`group border rounded-xl p-3 text-left hover:shadow-sm transition ${
                      value === name ? "border-[#0488AF]" : "border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Cmp size={24} className="text-[#0B4A8F]" />
                      <div className="font-medium">{name}</div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Lucide</div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------- Editor de líneas con botón azul "Añadir …" ---------- */
function LinesEditor({
  label,
  value = [],
  onChange,
  placeholder = "Texto…",
  placeholderPrefix = null,
  addLabel = "Añadir",
  hint = null,
}) {
  const arr = Array.isArray(value) && value.length ? value : [""];

  const addAt = (idx) => {
    const next = [...arr];
    next.splice(idx, 0, "");
    onChange(next);
  };
  const removeAt = (idx) => {
    const next = [...arr];
    next.splice(idx, 1);
    onChange(next.length ? next : [""]);
  };
  const updateAt = (idx, text) => {
    const next = [...arr];
    next[idx] = text;
    onChange(next);
  };

  const getPlaceholder = (index) => {
    if (placeholderPrefix) {
      return `${placeholderPrefix} ${index + 1}`;
    }
    return placeholder;
  };

  return (
    <div className="space-y-2">
      {label && (
        <div className="block text-sm font-medium text-[#0B4A8F] mb-1 flex items-center gap-1 relative pt-2">
          {label}
          {hint && <TooltipHelper text={hint} />}
        </div>
      )}
      {arr.map((line, i) => (
        <div key={i} className="flex items-start gap-2 min-w-0">
          <textarea
            className={`${textareaBase} min-h-[56px]`}
            value={line || ""}
            onChange={(e) => updateAt(i, e.target.value)}
            placeholder={getPlaceholder(i)}
          />
          <div className="flex flex-col gap-1 shrink-0">
            <button
              type="button"
              title="Añadir debajo"
              className="px-2 py-1.5 rounded-lg border hover:bg-gray-50"
              onClick={() => addAt(i + 1)}
            >
              <Plus size={16} />
            </button>
            <button
              type="button"
              title="Eliminar"
              className={`${btnIcon} text-red-600 hover:bg-red-50`}
              onClick={() => removeAt(i)}
              disabled={arr.length <= 1 && !arr[0]}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => addAt(arr.length)} className={btnAddBlue}>
        <Plus size={16} /> {addLabel}
      </button>
    </div>
  );
}

/* ======================================================= */
export default function EthicsReportsCard({ onBack }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    if (window.history.length > 1) return navigate(-1);
    navigate("/profile");
  };

  /* ===== Docs ===== */
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [versionTag, setVersionTag] = useState("");
  const [visibleNew, setVisibleNew] = useState(true);
  const fileRef = useRef(null);

  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editVersionTag, setEditVersionTag] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // feedback “Subiendo…”
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const progressTimerRef = useRef(null);
  const startFakeProgress = () => {
    stopFakeProgress();
    setUploadPct(10);
    progressTimerRef.current = setInterval(() => {
      setUploadPct((p) =>
        p >= 95 ? 95 : p + Math.max(1, Math.round((100 - p) * 0.05))
      );
    }, 180);
  };
  const stopFakeProgress = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  /* ===== Config ===== */
  const [cfg, setCfg] = useState({
    pageTitle: "",
    subtitle: "",
    lastReview: "",
    principlesVisible: true,
  });
  const [cfgInitial, setCfgInitial] = useState(cfg);
  const [cfgSaving, setCfgSaving] = useState(false);
  const [cfgLoading, setCfgLoading] = useState(false);
  const hasCfgChanges = useMemo(
    () => JSON.stringify(cfg) !== JSON.stringify(cfgInitial),
    [cfg, cfgInitial]
  );

  /* ===== Content ===== */
  const [cntLoading, setCntLoading] = useState(false);
  const [cntSaving, setCntSaving] = useState(false);
  const [cnt, setCnt] = useState({
    lastReview: "",
    principles: ["Integridad", "Respeto", "Responsabilidad", "Transparencia"],
    sections: [
      {
        title: "Propósito y alcance",
        icon: "ShieldCheck",
        body: [
          "Define estándares de conducta para directorio, colaboradores y terceros.",
          "Complementa leyes y políticas internas vigentes.",
        ],
      },
      {
        title: "Cumplimiento y anticorrupción",
        icon: "HandCoins",
        bullets: [
          "Prohibición de sobornos y pagos de facilitación.",
          "Relación con autoridades conforme a la ley.",
          "Registro fiel y oportuno de transacciones.",
        ],
      },
    ],
  });
  const [cntInit, setCntInit] = useState(cnt);
  const hasCntChanges = useMemo(
    () => {
      const contentChanged = JSON.stringify(cnt) !== JSON.stringify(cntInit);
      const principlesVisibleChanged = cfg.principlesVisible !== cfgInitial.principlesVisible;
      return contentChanged || principlesVisibleChanged;
    },
    [cnt, cntInit, cfg.principlesVisible, cfgInitial.principlesVisible]
  );

  const [pickerOpenFor, setPickerOpenFor] = useState(null);
  const [collapsed, setCollapsed] = useState(new Set());
  const [draggedPrincipleIndex, setDraggedPrincipleIndex] = useState(null);
  const toggleCollapsed = (idx) =>
    setCollapsed((prev) => {
      const next = new Set([...prev]);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  const collapseAll = () =>
    setCollapsed(new Set((cnt.sections || []).map((_, i) => i)));
  const expandAll = () => setCollapsed(new Set());
  
  // Drag and drop para principios
  const handlePrincipleDragStart = (e, index) => {
    setDraggedPrincipleIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
    e.target.style.opacity = "0.5";
  };
  
  const handlePrincipleDragEnd = (e) => {
    e.target.style.opacity = "";
    setDraggedPrincipleIndex(null);
  };
  
  const handlePrincipleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  
  const handlePrincipleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedPrincipleIndex === null || draggedPrincipleIndex === dropIndex) return;
    
    setCnt((c) => {
      const arr = [...(c.principles || [])];
      const [item] = arr.splice(draggedPrincipleIndex, 1);
      arr.splice(dropIndex, 0, item);
      return { ...c, principles: arr };
    });
    
    setDraggedPrincipleIndex(null);
  };

  /* ===== cargar todo ===== */
  async function loadAll() {
    try {
      setLoading(true);
      setCfgLoading(true);
      setCntLoading(true);
      const [docs, conf, content] = await Promise.all([
        listEthicsDocs(),
        getEthicsConfig().catch(() => null),
        getEthicsContent().catch(() => null),
      ]);

      setList(Array.isArray(docs) ? docs : []);

      // Si el backend devuelve el valor por defecto, tratarlo como vacío para mostrar placeholder
      const pageTitleFromBackend = conf?.pageTitle != null ? conf.pageTitle : "";
      const subtitleFromBackend = conf?.subtitle != null ? conf.subtitle : "";
      const cfgNext = {
        pageTitle: pageTitleFromBackend === "Código de Ética" ? "" : pageTitleFromBackend,
        subtitle: subtitleFromBackend,
        lastReview: String(conf?.lastReview ?? content?.lastReview ?? ""),
        principlesVisible: conf?.principlesVisible !== undefined ? !!conf.principlesVisible : true,
      };
      setCfg(cfgNext);
      setCfgInitial(cfgNext);

      if (content && typeof content === "object") {
        // Limpiar valores por defecto y strings vacíos
        const cleanSections = Array.isArray(content.sections) && content.sections.length
          ? content.sections.map((sec) => {
              const cleaned = { ...sec };
              // Limpiar body: filtrar strings vacíos y valores por defecto como "Contenido..."
              if (Array.isArray(cleaned.body)) {
                cleaned.body = cleaned.body
                  .map((p) => String(p).trim())
                  .filter((p) => p && !p.toLowerCase().includes("contenido"));
              }
              // Limpiar bullets: filtrar strings vacíos y valores por defecto
              if (Array.isArray(cleaned.bullets)) {
                cleaned.bullets = cleaned.bullets
                  .map((b) => String(b).trim())
                  .filter((b) => b && !b.toLowerCase().includes("contenido"));
              }
              // Normalizar objeto cta: asegurar que los campos existan incluso si están vacíos
              if (cleaned.cta && typeof cleaned.cta === "object") {
                cleaned.cta = {
                  reportUrl: String(cleaned.cta.reportUrl || "").trim(),
                  policyUrl: String(cleaned.cta.policyUrl || "").trim(),
                  ...(cleaned.cta.contactUrl ? { contactUrl: String(cleaned.cta.contactUrl).trim() } : {}),
                };
                // Si ambos campos principales están vacíos, eliminar el objeto cta
                if (!cleaned.cta.reportUrl && !cleaned.cta.policyUrl && !cleaned.cta.contactUrl) {
                  delete cleaned.cta;
                }
              }
              return cleaned;
            })
          : cnt.sections;
        
        const safe = {
          lastReview: String(content.lastReview ?? cfgNext.lastReview ?? ""),
          principles:
            Array.isArray(content.principles) && content.principles.length
              ? content.principles
              : cnt.principles,
          sections: cleanSections,
        };
        setCnt(safe);
        setCntInit(clone(safe));
        // Inicializar todas las secciones como contraídas
        const sectionsCount = (safe.sections || []).length;
        setCollapsed(new Set(Array.from({ length: sectionsCount }, (_, i) => i)));
      } else {
        const safe = { ...cnt, lastReview: cfgNext.lastReview || "" };
        setCnt(safe);
        setCntInit(clone(safe));
        // Inicializar todas las secciones como contraídas
        const sectionsCount = (safe.sections || []).length;
        setCollapsed(new Set(Array.from({ length: sectionsCount }, (_, i) => i)));
      }
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        e?.message || "No se pudieron cargar los datos de Código de Ética",
        "error"
      );
    } finally {
      setLoading(false);
      setCfgLoading(false);
      setCntLoading(false);
    }
  }
  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== Upload ===== */
  const pickFile = (f) => {
    if (!f) return;
    const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
    if (!isPdf) return Swal.fire("Archivo inválido", "Solo PDF.", "warning");
    if (f.size > MAX_MB * 1024 * 1024) {
      // Si es PDF, mostrar mensaje con link de compresión
      if (isPdf) {
        return Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          html: `El archivo "${f.name}" es demasiado grande (máx ${MAX_MB} MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
          confirmButtonText: "Entendido",
        });
      }
      return Swal.fire(
        "Archivo muy grande",
        `Máximo ${MAX_MB} MB (tiene ${fmtBytes(f.size)})`,
        "warning"
      );
    }
    setFile(f);
    if (!title.trim()) setTitle((f.name || "").replace(/\.pdf$/i, ""));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return Swal.fire("Atención", "Selecciona un PDF", "info");
    try {
      setUploading(true);
      startFakeProgress();
      await uploadEthicsDoc({
        file,
        title: title.trim() || undefined,
        versionTag: (versionTag || "").trim() || undefined,
        isPublished: !!visibleNew,
      });
      setUploadPct(100);
      await toast("Documento subido");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setTitle("");
      setVersionTag("");
      setVisibleNew(true);
      await loadAll();
    } catch (e) {
      Swal.fire("Error", e?.message || "No se pudo subir", "error");
    } finally {
      stopFakeProgress();
      setUploading(false);
      setTimeout(() => setUploadPct(0), 400);
    }
  };

  /* ===== Edit doc ===== */
  const beginEdit = (r) => {
    setEditingId(r.id);
    setEditTitle(r.title || "");
    setEditVersionTag(r.versionTag || "");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditVersionTag("");
    setSavingEdit(false);
  };
  const saveEdit = async (id) => {
    try {
      setSavingEdit(true);
      showLoading("Guardando…");
      await updateEthicsDoc(id, {
        title: (editTitle || "").trim() || undefined,
        versionTag: (editVersionTag || "").trim() || undefined,
      });
      Swal.close();
      await toast("Actualizado");
      cancelEdit();
      await loadAll();
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo actualizar", "error");
    } finally {
      setSavingEdit(false);
    }
  };
  const togglePublish = async (id, val) => {
    try {
      await updateEthicsDoc(id, { isPublished: !!val });
      await loadAll();
    } catch (e) {
      Swal.fire("Error", e?.message || "No se pudo cambiar estado", "error");
    }
  };
  const removeDoc = async (id) => {
    const doc = list.find((r) => r.id === id);
    const ok = await askConfirm({
      title: "Eliminar documento",
      text: `¿Seguro que deseas eliminar "${
        doc?.title || "este documento"
      }"? Esta acción no se puede deshacer.`,
      confirmButtonText: "Sí, eliminar",
    });
    if (!ok.isConfirmed) return;
    try {
      showLoading("Eliminando…");
      await deleteEthicsDoc(id);
      Swal.close();
      await toast("Eliminado");
      await loadAll();
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo eliminar", "error");
    }
  };

  /* ===== Config ===== */
  const saveConfig = async () => {
    try {
      setCfgSaving(true);
      showLoading("Guardando configuración…");

      const toSend = {
        pageTitle: (cfg?.pageTitle || "").trim(),
        subtitle: (cfg?.subtitle || "").trim(),
        lastReview: (cfg?.lastReview || "").trim(),
      };

      const savedCfg = await updateEthicsConfig(toSend);

      // Sincronizamos también en el contenido para que la página pública lo tome
      const contentPayload = {
        lastReview: toSend.lastReview,
        principles: Array.isArray(cnt.principles) ? cnt.principles : [],
        sections: Array.isArray(cnt.sections) ? cnt.sections : [],
      };
      const savedContent = await updateEthicsContent(contentPayload);

      // Si el backend devuelve el valor por defecto, tratarlo como vacío para mostrar placeholder
      const savedPageTitle = savedCfg?.pageTitle != null ? savedCfg.pageTitle : "";
      const savedSubtitle = savedCfg?.subtitle != null ? savedCfg.subtitle : "";
      const persistedCfg = {
        pageTitle: savedPageTitle === "Código de Ética" ? "" : savedPageTitle,
        subtitle: savedSubtitle,
        lastReview: savedCfg?.lastReview != null ? savedCfg.lastReview : toSend.lastReview,
        principlesVisible: cfg.principlesVisible !== undefined ? cfg.principlesVisible : (savedCfg?.principlesVisible !== undefined ? !!savedCfg.principlesVisible : true),
      };
      setCfg(persistedCfg);
      setCfgInitial(persistedCfg);

      const cleanCnt = {
        lastReview: String(savedContent?.lastReview ?? toSend.lastReview ?? ""),
        principles: Array.isArray(savedContent?.principles)
          ? savedContent.principles
          : contentPayload.principles,
        sections: Array.isArray(savedContent?.sections)
          ? savedContent.sections
          : contentPayload.sections,
      };
      setCnt(cleanCnt);
      setCntInit(cleanCnt);

      Swal.close();
      await toast("Configuración guardada");
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo guardar", "error");
    } finally {
      setCfgSaving(false);
    }
  };
  const discardConfig = () => setCfg(cfgInitial);

  /* ===== Content helpers ===== */
  const moveSection = (i, dir) =>
    setCnt((c) => {
      const arr = [...(c.sections || [])];
      const ni = i + dir;
      if (ni < 0 || ni >= arr.length) return c;
      const [it] = arr.splice(i, 1);
      arr.splice(ni, 0, it);
      return { ...c, sections: arr };
    });
  const addSection = () =>
    setCnt((c) => ({
      ...c,
      sections: [
        ...(c.sections || []),
        { title: "", icon: "FileText", body: [] },
      ],
    }));
  const removeSection = (i) =>
    setCnt((c) => ({
      ...c,
      sections: (c.sections || []).filter((_, idx) => idx !== i),
    }));
  const updateSectionField = (i, field, value) =>
    setCnt((c) => {
      const arr = [...(c.sections || [])];
      const obj = { ...(arr[i] || {}) };
      obj[field] = value;
      arr[i] = obj;
      return { ...c, sections: arr };
    });

  const saveContent = async () => {
    try {
      setCntSaving(true);
      showLoading("Guardando contenido…");

      const payload = clone(cnt);
      delete payload.id;
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.__v;

      payload.lastReview = String(payload.lastReview || "");
      payload.principles = (payload.principles || [])
        .map((s) => String(s).trim())
        .filter(Boolean);

      payload.sections = (payload.sections || [])
        .map((sec) => {
          const s = { ...sec };
          delete s.id;
          delete s.createdAt;
          delete s.updatedAt;
          delete s.__v;
          if (Array.isArray(s.body))
            s.body = s.body.map((p) => String(p).trim()).filter(Boolean);
          if (Array.isArray(s.bullets))
            s.bullets = s.bullets.map((b) => String(b).trim()).filter(Boolean);
          // Normalizar campos cta: preservar strings vacíos y eliminar solo si ambos están realmente vacíos
          if (s.cta) {
            const reportUrlTrimmed = String(s.cta.reportUrl || "").trim();
            const policyUrlTrimmed = String(s.cta.policyUrl || "").trim();
            // Si ambos campos están vacíos, eliminar el objeto cta
            if (!reportUrlTrimmed && !policyUrlTrimmed) {
              delete s.cta;
            } else {
              // Preservar los campos, normalizando undefined/null a string vacío
              s.cta = {
                reportUrl: reportUrlTrimmed || "",
                policyUrl: policyUrlTrimmed || "",
              };
              // Preservar contactUrl si existe
              if (s.cta.contactUrl) {
                s.cta.contactUrl = String(s.cta.contactUrl).trim();
              }
            }
          }
          if (!s.icon) delete s.icon;
          if (!s.iconImageUrl) delete s.iconImageUrl;
          if (!s.title?.trim()) s.title = "Sección";
          return s;
        })
        .filter(
          (s) =>
            s &&
            (s.title ||
              s.body?.length ||
              s.bullets?.length ||
              s.cta ||
              s.icon ||
              s.iconImageUrl)
        );

      const saved = await updateEthicsContent(payload);

      // Guardar también principlesVisible en el config
      if (cfg.principlesVisible !== undefined) {
        await updateEthicsConfig({
          principlesVisible: cfg.principlesVisible,
        });
        // Actualizar el estado inicial del config para reflejar el cambio
        setCfgInitial((prev) => ({ ...prev, principlesVisible: cfg.principlesVisible }));
      }

      const clean = {
        lastReview: String(saved?.lastReview ?? payload.lastReview ?? ""),
        principles: Array.isArray(saved?.principles)
          ? saved.principles
          : payload.principles,
        sections: Array.isArray(saved?.sections)
          ? saved.sections
          : payload.sections,
      };

      setCnt(clean);
      setCntInit(clean);
      Swal.close();
      await toast("Contenido actualizado");
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo guardar", "error");
    } finally {
      setCntSaving(false);
    }
  };
  const discardContent = () => setCnt(clone(cntInit));

  const ordered = useMemo(
    () =>
      [...list].sort((a, b) => {
        const ta = a?.createdAt ? +new Date(a.createdAt) : 0;
        const tb = b?.createdAt ? +new Date(b.createdAt) : 0;
        return tb - ta;
      }),
    [list]
  );

  /* ===== render ===== */
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6 overflow-x-hidden"
      >
        <button
          onClick={goBack}
          className="inline-flex items-center gap-3 px-3 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-xl text-white"
        >
          <ArrowLeft size={18} /> Volver al menú principal
        </button>

        <Banner
          title="Código de Ética"
          message="Sube, edita y controla visibilidad del documento. Ajusta encabezados y contenido público."
        />

        <SectionTips
          section="la sección de Código de Ética"
          buttonTitle="¿Cómo usar esta sección?"
          tipsTitle="¿Cómo usar el Código de Ética?"
          tipsList={[
            'En esta sección puedes subir el documento PDF del código de ética y controlar su visibilidad para los socios.',
            'Puedes personalizar los textos visibles en la página pública, como el título, subtítulo y última revisión.',
            'Puedes crear principios que se mostrarán en la página pública como chips reordenables.',
            'Puedes crear secciones con contenido personalizado, cada una con párrafos, puntos y botones de acción.',
            'Cada sección puede tener un ícono o imagen personalizada.',
            'Los documentos subidos se pueden mostrar u ocultar para los socios mediante el switch de visibilidad.',
          ]}
          finalNote="Consejo: Organiza bien las secciones y principios para crear una experiencia clara para los socios."
          color="#0488AF"
          videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766416932/Video_Tutorial_m%C3%B3dulo_Administraci%C3%B3n_-_C%C3%B3digo_de_%C3%89tica_inpaz4.mp4"
          manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412946/Manual_de_usuario_administraci%C3%B3n_bsvsyl.pdf"
        />

        {/* SUBIR + CONFIG (mismo layout que Prevention) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
          {/* Subir */}
          <div
            className="rounded-2xl p-[1.5px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5">
                <h2 className="text-lg font-semibold mb-3">
                  Subir documento (PDF)
                </h2>

                <form
                  onSubmit={handleUpload}
                  className="grid gap-3 md:grid-cols-3 items-end"
                >
                  <div className="md:col-span-1 flex items-end gap-2">
                    <label className={`${btnGhost} cursor-pointer`}>
                      <Upload size={16} />
                      Seleccionar archivo
                      <input
                        ref={fileRef}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => pickFile(e.target.files?.[0])}
                        disabled={uploading}
                      />
                    </label>
                  </div>

                  {file && (
                    <div className="md:col-span-3">
                      <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl border bg-slate-50">
                        <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                          <FileText className="w-5 h-5 text-[#0488AF]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">
                            {file.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`${btnGhost} shrink-0`}
                          onClick={() => {
                            setFile(null);
                            if (fileRef.current) fileRef.current.value = "";
                          }}
                          disabled={uploading}
                          title="Quitar archivo"
                        >
                          <X size={16} /> Quitar
                        </button>
                      </div>
                    </div>
                  )}

                  <label className="md:col-span-3">
                    <span className="block text-sm text-gray-700 mb-1">
                      Título (opcional)
                    </span>
                    <input
                      className={input}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Código de Ética de la organización"
                      disabled={uploading}
                    />
                  </label>

                  <label>
                    <span className="block text-sm text-gray-700 mb-1">
                      Etiqueta de versión (opcional)
                    </span>
                    <input
                      className={input}
                      value={versionTag}
                      onChange={(e) => setVersionTag(e.target.value)}
                      placeholder="v2025-10-26"
                      disabled={uploading}
                    />
                  </label>

                  <div className="md:col-span-3 flex flex-wrap items-center gap-3">

                    <button
                      type="submit"
                      disabled={!file || loading || uploading}
                      className={btnPri}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="animate-spin" size={16} />{" "}
                          Subiendo… {uploadPct ? `${uploadPct}%` : ""}
                        </>
                      ) : (
                        <>Subir documento</>
                      )}
                    </button>

                    {uploading && (
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[160px]">
                        <div
                          className="h-full bg-[#0488AF] transition-all"
                          style={{ width: `${uploadPct || 25}%` }}
                        />
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Config */}
          <div
            className="rounded-2xl p-[2px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #DCEBFF 0%, #DDF8FF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 min-w-0">
                  <h3 className="text-lg font-semibold">
                    Textos visibles en la página pública
                  </h3>
                  <button
                    type="button"
                    onClick={discardConfig}
                    disabled={!hasCfgChanges}
                    className={btnWarn}
                  >
                    <X size={14} /> Descartar cambios
                  </button>
                </div>

                <div className="rounded-xl border bg-[#F8FAFC] p-4 space-y-3">
                  <label className="block">
                    <span className="block text-sm text-gray-700 flex items-center gap-1">
                      Título
                      <TooltipHelper text='Si lo dejas vacío, se usará "Código de Ética".' />
                    </span>
                    <input
                      className={input}
                      value={cfg.pageTitle || ""}
                      onChange={(e) =>
                        setCfg((s) => ({ ...s, pageTitle: e.target.value }))
                      }
                      placeholder="Código de Ética"
                      disabled={cfgLoading}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm text-gray-700 flex items-center gap-1">
                      Subtítulo
                      <TooltipHelper text='Si lo dejas vacío, se usará "Nuestros principios, conductas esperadas y canal de integridad.". ста' />
                    </span>
                    <input
                      className={input}
                      value={cfg.subtitle || ""}
                      onChange={(e) =>
                        setCfg((s) => ({ ...s, subtitle: e.target.value }))
                      }
                      placeholder="Nuestros principios, conductas esperadas y canal de integridad."
                      disabled={cfgLoading}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm text-gray-700">
                      Última revisión (opcional)
                    </span>
                    <input
                      className={input}
                      value={cfg.lastReview || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCfg((s) => ({ ...s, lastReview: v }));
                        setCnt((c) => ({ ...c, lastReview: v }));
                      }}
                      placeholder="Octubre 2025"
                      disabled={cfgLoading}
                    />
                  </label>
                </div>

                <div className="pt-1">
                  <button
                    type="button"
                    onClick={saveConfig}
                    disabled={cfgSaving || !hasCfgChanges}
                    className={btnPri}
                  >
                    <Save size={16} /> Guardar configuración
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Divider />
        <Divider />

        {/* LISTA DOCS (mismo diseño que Prevention) */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Documentos subidos</h3>
        </div>
        {loading ? (
          <div className="text-gray-500 py-6">Cargando…</div>
        ) : (
          <div className="space-y-3">
            {ordered.length === 0 ? (
              <div className="text-gray-500 py-6">Aún no hay documentos.</div>
            ) : (
              ordered.map((r) => {
                const url = r.secureUrl || downloadEthicsDocUrl(r.id);
                const isEditing = editingId === r.id;
                return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border rounded-xl p-3 flex flex-col md:flex-row md:items-start md:justify-between gap-3 bg-white/70 min-w-0"
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="p-2 rounded-lg bg-blue-50 border border-blue-100 shrink-0">
                        <FileText className="w-5 h-5 text-[#0488AF]" />
                      </div>

                      {isEditing ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="px-2 py-1 rounded-lg bg-amber-100 text-amber-800 text-xs font-semibold">
                            Editando…
                          </span>
                          <input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className={`${input} w-full md:w-[16rem]`}
                            placeholder="Título"
                          />
                          <input
                            value={editVersionTag}
                            onChange={(e) => setEditVersionTag(e.target.value)}
                            className={`${input} w-full md:w-[10rem]`}
                            placeholder="v2025-10-26"
                          />
                        </div>
                      ) : (
                        <div className="min-w-0">
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="font-medium text-[#0488AF] hover:underline truncate break-words"
                              title={r.title?.trim() || "Código de Ética"}
                            >
                              {r.title?.trim() || "Código de Ética"}
                            </a>
                          ) : (
                            <span className="font-medium truncate break-words">
                              {r.title?.trim() || "Código de Ética"}
                            </span>
                          )}
                          <div className="text-xs text-gray-600">
                            {`Versión: ${r.versionTag || "—"} · ${
                              r.createdAt
                                ? new Date(r.createdAt).toLocaleString()
                                : ""
                            }`}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 md:justify-end shrink-0">
                      <div className="flex items-center gap-2 px-2 py-1 rounded border bg-white">
                        <Switch
                          checked={!!r.isPublished}
                          onChange={(v) => togglePublish(r.id, v)}
                        />
                        <span className="text-xs">
                          {r.isPublished
                            ? "Visible para socios"
                            : "Oculto para socios"}
                        </span>
                      </div>

                      {isEditing ? (
                        <>
                          <button
                            className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                            disabled={savingEdit}
                            onClick={() => saveEdit(r.id)}
                            title="Guardar"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                            onClick={cancelEdit}
                            title="Cancelar"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                          onClick={() => beginEdit(r)}
                          title="Editar"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}

                      {url && (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border hover:bg-gray-50"
                          title="Ver / Descargar"
                        >
                          <Eye size={16} /> Ver
                        </a>
                      )}

                      <button
                        className={btnDanger}
                        onClick={() => removeDoc(r.id)}
                        title="Eliminar"
                      >
                        <Trash2 size={16} /> Eliminar
                      </button>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        )}

        {/* CONTENIDO PÚBLICO (alineado a Prevention/Protocols) */}
        <div
          className="rounded-2xl p-[1.5px]"
          style={{
            background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
          }}
        >
          <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
            <div className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 min-w-0">
                <h3 className="text-lg font-semibold">
                  Contenido visible en la página pública
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={expandAll}
                    title="Expandir todo"
                  >
                    <Eye size={16} />{" "}
                    <span className="hidden sm:inline">Expandir todo</span>
                  </button>
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={collapseAll}
                    title="Contraer todo"
                  >
                    <EyeOff size={16} />{" "}
                    <span className="hidden sm:inline">Contraer todo</span>
                  </button>
                </div>
              </div>

              {/* PRINCIPIOS */}
              <div className="rounded-2xl border overflow-hidden mb-6">
                <div className="bg-gradient-to-r from-[#E6F0FF] to-[#E6FBFF] px-4 py-2 border-b flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-800">
                    Principios
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-gray-600">
                      {cfg.principlesVisible !== false ? "Visible para socios" : "Oculto para socios"}
                    </div>
                    <Switch
                      checked={cfg.principlesVisible !== false}
                      onChange={(v) => setCfg((s) => ({ ...s, principlesVisible: v }))}
                      titleOn="Principios visibles"
                      titleOff="Principios ocultos"
                    />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {(cnt.principles || []).map((p, i) => (
                      <div
                        key={i}
                        draggable
                        onDragStart={(e) => handlePrincipleDragStart(e, i)}
                        onDragEnd={handlePrincipleDragEnd}
                        onDragOver={handlePrincipleDragOver}
                        onDrop={(e) => handlePrincipleDrop(e, i)}
                        className={`group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white shadow-sm px-3 py-1.5 hover:border-[#0488AF] transition cursor-move ${
                          draggedPrincipleIndex === i ? "opacity-50" : ""
                        }`}
                      >
                        <span className="text-xs font-bold text-[#0B4A8F]">
                          {i + 1}
                        </span>
                        <input
                          className="bg-transparent outline-none min-w-[140px]"
                          value={p}
                          onChange={(e) =>
                            setCnt((c) => {
                              const arr = [...(c.principles || [])];
                              arr[i] = e.target.value;
                              return { ...c, principles: arr };
                            })
                          }
                          placeholder={`Principio ${i + 1}`}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="p-1 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                            disabled={i === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCnt((c) => {
                                const arr = [...(c.principles || [])];
                                const [it] = arr.splice(i, 1);
                                arr.splice(i - 1, 0, it);
                                return { ...c, principles: arr };
                              });
                            }}
                            title="Subir"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                            disabled={i === (cnt.principles?.length || 0) - 1}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCnt((c) => {
                                const arr = [...(c.principles || [])];
                                const [it] = arr.splice(i, 1);
                                arr.splice(i + 1, 0, it);
                                return { ...c, principles: arr };
                              });
                            }}
                            title="Bajar"
                          >
                            <ChevronDown size={14} />
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded-lg border text-red-600 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCnt((c) => ({
                                ...c,
                                principles: (c.principles || []).filter(
                                  (_, idx) => idx !== i
                                ),
                              }));
                            }}
                            title="Eliminar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                    {(cnt.principles || []).length === 0 && (
                      <div className="text-sm text-gray-500">
                        Sin principios — agrega al menos uno.
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() =>
                        setCnt((c) => ({
                          ...c,
                          principles: [
                            ...(c.principles || []),
                            "",
                          ],
                        }))
                      }
                      className={btnAddBlue}
                    >
                      <Plus size={16} /> Agregar principio
                    </button>
                  </div>
                </div>
              </div>

              {/* SECCIONES */}
              <div className="rounded-2xl border overflow-hidden">
                <div className="bg-gradient-to-r from-[#E6F0FF] to-[#E6FBFF] px-4 py-2 border-b flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-800">
                    Secciones
                  </div>
                </div>

                <div className="p-4 space-y-6">
                  {(cnt.sections || []).map((sec, i) => {
                    const isCollapsed = collapsed.has(i);
                    return (
                      <div
                        key={i}
                        className="rounded-2xl border shadow-sm overflow-hidden"
                      >
                        <div className="h-1 w-full bg-gradient-to-r from-[#0488AF] to-[#C42251]" />

                        <div className="p-3 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <button
                              type="button"
                              onClick={() => toggleCollapsed(i)}
                              className="flex items-center gap-2 font-medium text-left hover:underline min-w-0 flex-1"
                              title={
                                isCollapsed
                                  ? "Expandir sección"
                                  : "Contraer sección"
                              }
                            >
                              {(sec.iconImageUrl || sec.icon) && (
                                <span className="flex-shrink-0">
                                  {sec.iconImageUrl ? (
                                    <img
                                      src={sec.iconImageUrl}
                                      alt=""
                                      className="h-5 w-5 object-contain rounded"
                                    />
                                  ) : (
                                    <IconPreview name={sec.icon} size={20} />
                                  )}
                                </span>
                              )}
                              <span className="truncate">{sec.title || "Sección"}</span>
                            </button>
                            <div className="flex flex-wrap items-center gap-1">
                              <button
                                className={btnIcon}
                                onClick={() => toggleCollapsed(i)}
                                title={isCollapsed ? "Expandir" : "Contraer"}
                              >
                                {isCollapsed ? (
                                  <Eye size={16} />
                                ) : (
                                  <EyeOff size={16} />
                                )}
                              </button>
                              <button
                                className={btnIcon}
                                disabled={i === 0}
                                onClick={() => moveSection(i, -1)}
                                title="Subir"
                              >
                                <ChevronUp size={16} />
                              </button>
                              <button
                                className={btnIcon}
                                disabled={
                                  i === (cnt.sections?.length || 0) - 1
                                }
                                onClick={() => moveSection(i, +1)}
                                title="Bajar"
                              >
                                <ChevronDown size={16} />
                              </button>
                              <button
                                className={btnDanger}
                                onClick={() => removeSection(i)}
                                title="Eliminar sección"
                              >
                                <Trash2 size={16} />{" "}
                                <span className="hidden sm:inline">
                                  Eliminar
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {!isCollapsed && (
                          <div className="p-4 space-y-3">
                            <label>
                              <span className="block text-sm text-gray-700 mb-1">
                                Título
                              </span>
                              <input
                                className={input}
                                value={sec.title || ""}
                                onChange={(e) =>
                                  updateSectionField(
                                    i,
                                    "title",
                                    e.target.value
                                  )
                                }
                                placeholder="Título de nueva sección"
                              />
                            </label>

                            <div className="space-y-2">
                              <span className="block text-sm text-gray-700">
                                Ícono (Lucide) o Imagen
                              </span>
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="flex items-center gap-2 rounded-xl border px-3 py-1.5 bg-gray-50">
                                  <IconPreview name={sec.icon} />
                                  <code className="text-xs">
                                    {sec.icon || "—"}
                                  </code>
                                </div>
                                <button
                                  type="button"
                                  className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                                  onClick={() => setPickerOpenFor(i)}
                                  title="Elegir ícono"
                                >
                                  <Wand2 size={16} /> Elegir ícono
                                </button>
                                <button
                                  type="button"
                                  className={btnIcon}
                                  onClick={() =>
                                    updateSectionField(i, "icon", "")
                                  }
                                  title="Quitar ícono"
                                >
                                  <X size={16} />
                                </button>
                              </div>

                              <div className="flex flex-wrap items-center gap-2">
                                <ImageIcon
                                  size={16}
                                  className="text-gray-500"
                                />
                                <input
                                  className={input}
                                  value={sec.iconImageUrl || ""}
                                  onChange={(e) =>
                                    updateSectionField(
                                      i,
                                      "iconImageUrl",
                                      e.target.value
                                    )
                                  }
                                  placeholder="https://…/icon.png (opcional)"
                                />
                                {sec.iconImageUrl && (
                                  <button
                                    type="button"
                                    className={btnIcon}
                                    onClick={() =>
                                      updateSectionField(
                                        i,
                                        "iconImageUrl",
                                        ""
                                      )
                                    }
                                    title="Quitar imagen"
                                  >
                                    <X size={16} />
                                  </button>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                Si indicas una imagen, tendrá prioridad sobre
                                el ícono.
                              </div>
                            </div>

                            <div className="grid gap-4 md:grid-cols-2 mt-8">
                              <LinesEditor
                                label="Párrafos"
                                value={Array.isArray(sec.body) ? sec.body : []}
                                onChange={(arr) =>
                                  updateSectionField(i, "body", arr)
                                }
                                placeholderPrefix="Párrafo"
                                addLabel="Añadir párrafo"
                                hint="Cada párrafo aparecerá como un bloque de texto separado en la página pública."
                              />
                              <LinesEditor
                                label="Punteo"
                                value={
                                  Array.isArray(sec.bullets)
                                    ? sec.bullets
                                    : []
                                }
                                onChange={(arr) =>
                                  updateSectionField(i, "bullets", arr)
                                }
                                placeholderPrefix="Punto"
                                addLabel="Añadir punto"
                                hint="Cada punto aparecerá como un elemento de lista con viñeta en la página pública."
                              />
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <label>
                                <span className="block text-sm text-gray-700 mb-1 flex items-center gap-1">
                                  Botón de acción: Reportar (URL o mailto)
                                  <TooltipHelper text="Para correos usa mailto:correo@ejemplo.com. Para páginas web usa http:// o https://" />
                                </span>
                                <input
                                  className={input}
                                  value={sec.cta?.reportUrl || ""}
                                  onChange={(e) =>
                                    updateSectionField(i, "cta", {
                                      reportUrl: e.target.value || "",
                                      policyUrl: sec.cta?.policyUrl || "",
                                      ...(sec.cta?.contactUrl ? { contactUrl: sec.cta.contactUrl } : {}),
                                    })
                                  }
                                  placeholder="mailto:etica@empresa.cl"
                                />
                                {sec.cta?.reportUrl &&
                                  !isValidCtaUrl(sec.cta.reportUrl) && (
                                    <div className="text-xs text-rose-700 mt-1">
                                      Debe comenzar con http(s)://, / o mailto:
                                    </div>
                                  )}
                              </label>
                              <label>
                                <span className="block text-sm text-gray-700 mb-1 flex items-center gap-1">
                                  Botón de acción: Política (URL o /ruta)
                                  <TooltipHelper text="Ruta interna (/) o URL externa (http:// o https://)" />
                                </span>
                                <input
                                  className={input}
                                  value={sec.cta?.policyUrl || ""}
                                  onChange={(e) =>
                                    updateSectionField(i, "cta", {
                                      reportUrl: sec.cta?.reportUrl || "",
                                      policyUrl: e.target.value || "",
                                      ...(sec.cta?.contactUrl ? { contactUrl: sec.cta.contactUrl } : {}),
                                    })
                                  }
                                  placeholder="/politicas/linea-de-etica"
                                />
                                {sec.cta?.policyUrl &&
                                  !isHttpOrRoute(sec.cta.policyUrl) && (
                                    <div className="text-xs text-rose-700 mt-1">
                                      Debe comenzar con http(s):// o /
                                    </div>
                                  )}
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(cnt.sections || []).length === 0 && (
                    <div className="text-sm text-gray-500">
                      Sin secciones — agrega al menos una.
                    </div>
                  )}
                </div>

                <div className="px-4 py-3 bg-gray-50 border-t">
                  <button
                    type="button"
                    onClick={addSection}
                    className={btnAddBlue}
                  >
                    <Plus size={16} /> Agregar sección
                  </button>
                </div>
              </div>

              {/* Botón Guardar fuera de las secciones */}
              <div className="px-5 pb-5 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2 justify-end">
                  <button
                    type="button"
                    onClick={saveContent}
                    disabled={cntSaving || !hasCntChanges}
                    className={btnPri}
                  >
                    <Save size={16} /> Guardar contenido
                  </button>
                  <button
                    type="button"
                    onClick={discardContent}
                    disabled={cntSaving || !hasCntChanges}
                    className={btnGhost}
                  >
                    <X size={16} /> Descartar cambios
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Modal IconPicker */}
      <IconPicker
        open={pickerOpenFor != null}
        value={
          pickerOpenFor != null ? cnt.sections?.[pickerOpenFor]?.icon : undefined
        }
        onSelect={(name) => {
          if (pickerOpenFor == null) return;
          setCnt((c) => {
            const arr = [...(c.sections || [])];
            arr[pickerOpenFor] = { ...(arr[pickerOpenFor] || {}), icon: name };
            return { ...c, sections: arr };
          });
        }}
        onClose={() => setPickerOpenFor(null)}
      />
    </>
  );
}
